'use client'

import { useRef } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/shared/SectionLabel'

export function Rail({
  eyebrow,
  title,
  children,
  tight = false,
}: {
  eyebrow: React.ReactNode
  title: React.ReactNode
  children: React.ReactNode
  tight?: boolean
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollBy({ left: el.clientWidth * 0.8 * dir, behavior: 'smooth' })
  }

  return (
    <section className={cn(tight ? 'pt-2.5' : 'pt-8')}>
      <div className="mb-4 flex items-end justify-between pl-[var(--mp-pad-l)] pr-[var(--mp-pad-r)]">
        <div>
          <SectionLabel className="mb-1">{eyebrow}</SectionLabel>
          <h2 className="text-2xl font-bold tracking-tight text-text-strong">{title}</h2>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="grid size-[34px] place-items-center rounded-full border border-atrium-border bg-white text-atrium-black transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40 dark:bg-atrium-background"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} className="size-[17px]" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="grid size-[34px] place-items-center rounded-full border border-atrium-black bg-atrium-black text-white transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40 dark:text-atrium-background"
          >
            <HugeiconsIcon icon={ArrowRight01Icon} className="size-[17px]" />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        // CRITICAL: do NOT set padding-left on this flex+overflow-x:auto
        // container. Chromium drops the inline-start padding when the
        // scroller has flex children, which pushes the first card past the
        // section title. Apply the inset to the first child's margin-left
        // (and scroll-padding-left on the scroller for snap correctness).
        className="flex gap-3.5 overflow-x-auto pb-[18px] pt-1 [&::-webkit-scrollbar]:hidden [scroll-padding-left:var(--mp-pad-l)] [scroll-snap-type:x_mandatory] [scrollbar-width:none] [&>:first-child]:ml-[var(--mp-pad-l)] [&>:last-child]:mr-[var(--mp-pad-r)]"
      >
        {children}
      </div>
    </section>
  )
}
